import { useTranslation } from "react-i18next";
import { useRef, useState } from "react";
import { Spinner } from "@/components/ui/modal";
import { useI18nNavigate } from "@/hooks/use-i18n-navigate";
import { useAPISendVerifyCode } from "@/hooks/use-api-send-verify-code";
import { useAPIEmailLogin } from "@/hooks/use-api-email-login";
import { iamLoginMethodManager, iamVerifyCodeManager, iamInviteIamEmailManager } from "@/manager";
import analytics from "@/utils/analytics";

interface EmailVerifySectionProps {
  agentId?: string;
}

export function EmailVerifySection({ agentId }: EmailVerifySectionProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState("input"); // input | verify
  const [email, setEmail] = useState(iamInviteIamEmailManager.get() || "");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendCodedown, setResendCodedown] = useState(0);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [sendEmailCount, setSendEmailCount] = useState(0);
  const [sendCodeCount, setSendCodeCount] = useState(0);
  const emailRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const resendTimer = useRef(null);
  const blockTimer = useRef(null);
  const navigate = useI18nNavigate();
  const { handleSendVerifyCode } = useAPISendVerifyCode();
  const { handleEmailLogin } = useAPIEmailLogin();

  // 邮箱正则
  const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  // 发送邮箱验证码
  const sendEmail = async (force = false) => {
    if (!isEmail(email)) {
      return;
    }
    if (sendEmailCount >= 10 && !force) {
      setError(t("login.email_verify_section.frequent_requests"));
      blockTimer.current = setTimeout(() => {
        setSendEmailCount(0);
        setError("");
      }, 60000);
      return;
    }
    analytics.trackEmailLogin({ email });
    setLoading(true);
    setError("");
    setInfo("");
    try {
      await handleSendVerifyCode(email, agentId);
    } catch (error) {
      analytics.trackEmailLoginFail({ reason: error.message, email });
      setSendEmailCount((c) => c + 1);
      setLoading(false);
      setError(error.message);
      requestAnimationFrame(() => {
        emailRef.current?.focus();
      });
      return;
    }
    setSendEmailCount((c) => c + 1);
    setResendCodedown(60);
    setStep("verify");
    setInfo(t("login.email_verify_section.code_sent"));
    setLoading(false);
    if (resendTimer.current) clearInterval(resendTimer.current);
    resendTimer.current = setInterval(() => {
      setResendCodedown((t) => {
        if (t <= 1) {
          clearInterval(resendTimer.current);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
  };

  // 验证邮箱验证码
  const verifyCode = async () => {
    if (!code) {
      return;
    }
    if (sendCodeCount >= 10) {
      setError(t("login.email_verify_section.frequent_requests"));
      blockTimer.current = setTimeout(() => {
        setSendCodeCount(0);
        setError("");
      }, 60000);
      return;
    }
    setLoading(true);
    setError("");
    setInfo("");
    try {
      await handleEmailLogin(code, agentId);
    } catch (error) {
      // 邀请码错误需拦截处理（后期会移除）
      if (error.message === "ACTIVATION_CODE_NEED") {
        // 在这之前应该已经设置好了email和sessionId
        iamVerifyCodeManager.update(code);
        iamLoginMethodManager.update(2);
        navigate("/invite/");
        return;
      }
      analytics.trackEmailLoginFail({ reason: error.message, email });
      setSendCodeCount((c) => c + 1);
      setLoading(false);
      setError(error.message);
      requestAnimationFrame(() => {
        codeRef.current?.focus();
      });
      return;
    }
    analytics.trackEmailLoginSuccess({ email });
    setSendCodeCount((c) => c + 1);
    setLoading(false);
    setInfo(t("login.email_verify_section.login_successful"));
    navigate("/");
  };

  // 返回上一步
  const reset = () => {
    setStep("input");
    setCode("");
    setError("");
    setInfo("");
    setResendCodedown(0);
    if (resendTimer.current) clearInterval(resendTimer.current);
    if (blockTimer.current) clearTimeout(blockTimer.current);
  };

  // 重发验证码
  const resend = () => {
    if (resendCodedown === 0) {
      sendEmail(true);
    }
  };

  // 修改邮箱
  const input = (v: string) => {
    setEmail(v);
    if (step === "verify") {
      reset();
    }
  };

  return (
    <form className="w-full flex flex-col items-center transition-all duration-500">
      {/* 邮箱输入 */}
      <div className="relative w-full mb-5">
        <input
          ref={emailRef}
          type="email"
          autoFocus
          className="w-full bg-[rgba(245,245,245,1)]  border-gray-700 rounded-lg pl-4 pr-4 py-3 text-[rgba(107,107,107,1)] placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[rgba(0,0,0,1)] focus:border-transparent transition-all"
          aria-label="Email Address"
          placeholder={t("login.email_verify_section.email_placeholder")}
          value={email}
          onChange={(e) => input(e.target.value)}
          disabled={loading}
        />
      </div>

      {/* 验证码输入框 */}
      {step === "verify" && (
        <div className="relative w-full mb-5">
          <input
            ref={codeRef}
            type="text"
            autoFocus
            className="w-full bg-[rgba(245,245,245,1)] border-gray-700 rounded-lg pl-4 pr-4 py-3 text-[rgba(107,107,107,1)] placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[rgba(0,0,0,1)] focus:border-transparent transition-all"
            aria-label="Verification Code"
            placeholder={t("login.email_verify_section.code_placeholder")}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={loading}
          />
        </div>
      )}

      {/* 错误/提示 */}
      {error && (
        <div className="w-full mb-4 text-sm text-center text-red-400">
          {error}
        </div>
      )}
      {info && (
        <div className="w-full mb-4 text-sm text-center text-green-400">
          {info}
        </div>
      )}

      {step === "verify" && (
        <div className="w-full text-right mb-4">
          <button
            type="button"
            className="text-i-primary hover:text-i-primary-hover text-sm transition-colors"
            onClick={resend}
            disabled={resendCodedown > 0 || loading}
          >
            {resendCodedown > 0
              ? t("login.email_verify_section.resend_in", { seconds: resendCodedown })
              : t("login.email_verify_section.resend_code")}
          </button>
        </div>
      )}

      {/* 登录按钮 */}
      {step === "input" && (
        <button
          type="submit"
          className={`w-full py-3 rounded-lg text-white text-base font-medium transition-all flex items-center justify-center ${isEmail(email)
            ? "bg-[rgba(0,0,0,1)] hover:bg-[#000000cc] cursor-pointer"
            : "bg-i-placeholder cursor-not-allowed"
            }`}
          disabled={!isEmail(email) || loading}
          onClick={(e) => {
            e.preventDefault();
            sendEmail();
          }}
        >
          {loading ? <Spinner size={20} className="mr-2" /> : t("login.email_verify_section.login")}
        </button>
      )}

      {step === "verify" && (
        <button
          type="submit"
          className={`w-full py-3 rounded-lg text-white text-base font-medium transition-all flex items-center justify-center ${code
            ? "bg-i-primary hover:bg-i-primary-hover"
            : "bg-i-placeholder cursor-not-allowed"
            }`}
          disabled={!code || loading}
          onClick={(e) => {
            e.preventDefault();
            verifyCode();
          }}
        >
          {loading ? <Spinner size={20} className="mr-2" /> : t("login.email_verify_section.login")}
        </button>
      )}
    </form>
  );
}
